// OrderMaintenanceSection
// ----------------------------------------------------------------------------
// One-click UI for the two Phase-XX order clean-up migrations that live on
// `/api/ultra-admin/maintenance/*`. Used for back-filling:
//
//  1. Orders stuck on the legacy hardcoded `supplier_tenant_id="quattro-stand"`
//     value — we re-route them to the real supplier based on which product(s)
//     the order actually contains.
//  2. Paid direct-orders that never graduated out of "Onay Bekliyor" because
//     the auto-confirm logic didn't exist when they were created.
//
// The flow for each action is strictly PREVIEW → APPLY:
//   • Button "Önizle"        → calls the endpoint with apply=false
//   • User reviews the count & list the server returned
//   • Button "Şimdi Uygula"  → calls again with apply=true, writes to DB
//
// Sending apply=true without previewing first is prevented by disabling the
// button until a preview has been run in the current session — mirrors the
// guard rails we use on the Danger Zone.
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Wrench, RefreshCw, CheckCircle2, AlertTriangle, PlayCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAdminToken() {
  return localStorage.getItem('cop31_ultra_token') || '';
}

function MaintenanceCard({
  title,
  description,
  endpoint,
  previewKey,
  tr,
  resultFormatter,
}) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingApply, setLoadingApply] = useState(false);
  const [appliedSummary, setAppliedSummary] = useState(null);

  const runPreview = async () => {
    setLoadingPreview(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(`${API}${endpoint}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
      setAppliedSummary(null); // preview invalidates any prior apply result
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Önizleme başarısız.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const runApply = async () => {
    if (!preview) return;
    const count = Number(preview?.[previewKey] || 0);
    const ok = window.confirm(
      tr
        ? `${count} sipariş güncellenecek. Onaylıyor musunuz?`
        : `${count} order(s) will be updated. Proceed?`
    );
    if (!ok) return;

    setLoadingApply(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(`${API}${endpoint}?apply=true`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppliedSummary(res.data);
      setPreview(null); // force a new preview before next apply
      toast.success(res.data.note || (tr ? 'Uygulandı.' : 'Applied.'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Uygulama başarısız.');
    } finally {
      setLoadingApply(false);
    }
  };

  const affected = Number(preview?.[previewKey] || 0);
  const canApply = preview !== null && affected > 0 && !loadingApply;

  return (
    <Card className="border-0 shadow-sm rounded-2xl overflow-hidden">
      <CardContent className="p-0">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
              <Wrench className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900">{title}</h3>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="p-5 bg-gray-50/60 flex flex-wrap gap-3">
          <Button
            data-testid={`${endpoint.split('/').pop()}-preview-btn`}
            onClick={runPreview}
            disabled={loadingPreview}
            variant="outline"
            className="rounded-xl gap-2"
          >
            {loadingPreview
              ? <RefreshCw className="h-4 w-4 animate-spin" />
              : <PlayCircle className="h-4 w-4" />}
            {tr ? 'Önizle (dry-run)' : 'Preview (dry-run)'}
          </Button>
          <Button
            data-testid={`${endpoint.split('/').pop()}-apply-btn`}
            onClick={runApply}
            disabled={!canApply}
            className="rounded-xl gap-2 bg-amber-600 hover:bg-amber-700 text-white disabled:bg-gray-200 disabled:text-gray-400"
            title={
              !preview
                ? (tr ? 'Önce önizleme çalıştırın' : 'Run a preview first')
                : affected === 0
                ? (tr ? 'Güncellenecek kayıt yok' : 'Nothing to update')
                : undefined
            }
          >
            {loadingApply
              ? <RefreshCw className="h-4 w-4 animate-spin" />
              : <CheckCircle2 className="h-4 w-4" />}
            {tr ? 'Şimdi Uygula' : 'Apply Now'}
          </Button>
        </div>

        {/* Preview summary */}
        {preview && (
          <div className="p-5 border-t border-gray-100 bg-amber-50/30">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-900">
                {tr ? 'Önizleme Sonucu' : 'Preview Result'}
              </span>
            </div>
            {resultFormatter(preview, tr)}
          </div>
        )}

        {/* Applied summary */}
        {appliedSummary && (
          <div className="p-5 border-t border-gray-100 bg-emerald-50/40">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-900">
                {tr ? 'Uygulama Tamamlandı' : 'Applied Successfully'}
              </span>
            </div>
            <p className="text-xs text-emerald-700">{appliedSummary.note}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function rerouteResult(data, tr) {
  return (
    <div className="space-y-2 text-xs text-gray-600">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        <strong>{data.rerouted_count}</strong>
        {' '}{tr ? 'sipariş doğru tedarikçiye yönlendirilecek' : 'order(s) will be rerouted'}
      </div>
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-amber-500" />
        <strong>{data.needs_manual_split.length}</strong>
        {' '}{tr ? 'sipariş manuel inceleme istiyor (birden çok tedarikçi)' : 'need manual split (multi-supplier)'}
      </div>
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-gray-400" />
        <strong>{data.organizer_only.length}</strong>
        {' '}{tr ? 'organizer siparişi (dokunulmayacak)' : 'organizer-only (unchanged)'}
      </div>
      {data.rerouted.length > 0 && (
        <details className="mt-2 bg-white rounded-lg p-2 border border-gray-100">
          <summary className="cursor-pointer text-gray-500 select-none">
            {tr ? 'Detay (ilk 10)' : 'Details (first 10)'}
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] font-mono text-gray-700">
            {data.rerouted.slice(0, 10).map((r) => (
              <li key={r.order_id} className="truncate">
                #{r.order_number} · {r.current_tenant} → {r.new_tenant}
                {r.payment_status === 'paid' && <span className="ml-1 text-emerald-600">✓paid</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function autoConfirmResult(data, tr) {
  return (
    <div className="space-y-2 text-xs text-gray-600">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        <strong>{data.promoted_count}</strong>
        {' '}{tr ? 'paid sipariş otomatik "Onaylandı"ya yükseltilecek' : 'paid orders will auto-confirm'}
      </div>
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-amber-500" />
        <strong>{data.has_manual_item.length}</strong>
        {' '}{tr ? 'sipariş manuel onay gerektiren ürün içeriyor (dokunulmayacak)' : 'contain manual-review items (unchanged)'}
      </div>
      {data.promoted.length > 0 && (
        <details className="mt-2 bg-white rounded-lg p-2 border border-gray-100">
          <summary className="cursor-pointer text-gray-500 select-none">
            {tr ? 'Detay (ilk 10)' : 'Details (first 10)'}
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] font-mono text-gray-700">
            {data.promoted.slice(0, 10).map((r) => (
              <li key={r.order_id} className="truncate">
                #{r.order_number} · {r.currency} {r.total_amount?.toLocaleString?.()}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function orphanNotifResult(data, tr) {
  return (
    <div className="space-y-2 text-xs text-gray-600">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-red-500" />
        <strong>{data.orphan_count}</strong>
        {' '}{tr ? 'hayalet bildirim (silinmiş siparişleri işaret ediyor)' : 'orphan notifications (pointing to deleted orders)'}
      </div>
      {Array.isArray(data.sample) && data.sample.length > 0 && (
        <details className="mt-2 bg-white rounded-lg p-2 border border-gray-100">
          <summary className="cursor-pointer text-gray-500 select-none">
            {tr ? 'Örnek (ilk 20)' : 'Sample (first 20)'}
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] font-mono text-gray-700">
            {data.sample.map((r) => (
              <li key={r.notification_id} className="truncate">
                #{r.order_number || '(no number)'} · order_id={r.order_id?.slice(0, 8)}…
                {' → user '}{r.user_id?.slice(0, 8)}…
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default function OrderMaintenanceSection({ tr }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          {tr ? 'Sipariş Bakımı' : 'Order Maintenance'}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {tr
            ? 'Yeni deploylar öncesi oluşmuş siparişleri güncel akışa hizalar. Önce "Önizle" ile ne değişeceğini gör, sonra "Şimdi Uygula" ile yaz.'
            : 'Align legacy orders with the new checkout flow. Preview first, then apply.'}
        </p>
      </div>

      <MaintenanceCard
        tr={tr}
        title={tr ? 'Yanlış Tedarikçiye Düşmüş Siparişleri Yönlendir' : 'Reroute Stale Orders'}
        description={
          tr
            ? 'Eski sistemde tüm siparişler varsayılan bir tenant\'a ("quattro-stand") yazılıyordu. Bu, içindeki ürünlerin gerçek tedarikçisine göre siparişi doğru tenant\'a taşır. Güvenli: sadece tek tedarikçili siparişleri dokunur, çok-tedarikçili olanları rapor eder.'
            : 'Legacy orders all landed on a placeholder tenant ("quattro-stand"). This re-routes them to the supplier that actually owns the products. Safe: only touches single-supplier orders, reports multi-supplier ones for manual review.'
        }
        endpoint="/ultra-admin/maintenance/reroute-stale-orders"
        previewKey="rerouted_count"
        resultFormatter={rerouteResult}
      />

      <MaintenanceCard
        tr={tr}
        title={tr ? 'Ödenmiş Siparişleri Otomatik Onayla' : 'Auto-confirm Paid Orders'}
        description={
          tr
            ? 'Ödendiği halde "Onay Bekliyor" kalan siparişleri, içinde manuel onay gerektiren ürün yoksa otomatik "Onaylandı"ya yükseltir. Katalogdan standart ürün alan müşterilere artık beklemek zorunda kalmaz.'
            : 'Paid orders stuck on "Awaiting Approval" get promoted to "Confirmed" — unless they contain products flagged for manual review.'
        }
        endpoint="/ultra-admin/maintenance/autoconfirm-paid-orders"
        previewKey="promoted_count"
        resultFormatter={autoConfirmResult}
      />

      <MaintenanceCard
        tr={tr}
        title={tr ? 'Hayalet Bildirimleri Temizle' : 'Clean Up Ghost Notifications'}
        description={
          tr
            ? 'Silinmiş siparişler için geçmişte tedarikçilere / müşterilere düşmüş ama referans ettiği sipariş artık olmayan bildirimleri temizler. Zil ikonundaki "hayalet" kırmızı rozetleri sıfırlar.'
            : 'Deletes notifications that reference orders that no longer exist (e.g., old supplier "New Order" alerts pointing to deleted orders). Clears ghost bell badges.'
        }
        endpoint="/ultra-admin/maintenance/cleanup-orphan-order-notifications"
        previewKey="orphan_count"
        resultFormatter={orphanNotifResult}
      />
    </div>
  );
}
